// ts-rest contract for the two read-only reference pages of the Admin shell.
//
// - Access: who can read and change which data. It is the data part of the
//   access grid (SEP-255): the Object Types, the Actions and the agents, as
//   the registry of the running server holds them. The HTTP routes are not in
//   it, because the server finds them in its source files, and a deployed
//   image holds no source files. `docs/access-grid.json` keeps the full grid.
// - Features: the bought packages, the add-ons, the AI seats and the state of
//   each feature of the catalogue in THIS deployment, with the release holds.
//
// Endpoints:
//   GET /api/admin/access    — the data access grid
//   GET /api/admin/features  — the features of this deployment
//
// The controller gates both on the admin persona (ADR-0004). This is a
// presentation projection, NOT a domain model (ADR-0003).

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

// ---------------------------------------------------------------------------
// Access
// ---------------------------------------------------------------------------

export const accessPersonaSchema = z.object({
    key: z.string(),
    label: z.string(),
    description: z.string(),
});
export type AccessPersona = z.infer<typeof accessPersonaSchema>;

export const accessActionSchema = z.object({
    key: z.string(),
    name: z.string(),
    summary: z.string(),
    /** Null when the Action declares no personas: each staff persona can run it. */
    personas: z.array(z.string()).nullable(),
});
export type AccessAction = z.infer<typeof accessActionSchema>;

export const accessObjectTypeSchema = z.object({
    key: z.string(),
    name: z.string(),
    summary: z.string(),
    area: z.string(),
    /** The ontology package that holds the Object Type. */
    layer: z.string().nullable(),
    containsClientData: z.boolean().nullable(),
    /** The read gate of `ctx.read`. `'*'` is each staff persona. */
    read: z.array(z.string()),
    /** How far a Desk read goes (ADR-0005). Null when Desk cannot read. */
    deskRowScope: z.string().nullable(),
    actions: z.array(accessActionSchema),
});
export type AccessObjectType = z.infer<typeof accessObjectTypeSchema>;

export const accessAgentSchema = z.object({
    name: z.string(),
    persona: z.string(),
    reads: z.array(z.string()),
    /** The Object Types the agent reads past the persona gate. */
    privilegedReads: z.array(z.string()),
});
export type AccessAgent = z.infer<typeof accessAgentSchema>;

export const accessGridResponseSchema = z.object({
    personas: z.array(accessPersonaSchema),
    objectTypes: z.array(accessObjectTypeSchema),
    /** The Actions with no target Object Type. */
    firmActions: z.array(accessActionSchema),
    agents: z.array(accessAgentSchema),
});
export type AccessGridResponse = z.infer<typeof accessGridResponseSchema>;

// ---------------------------------------------------------------------------
// Features
// ---------------------------------------------------------------------------

/**
 * The state of one feature in this deployment:
 * - `on`: the manifest enables it, and no release hold applies.
 * - `held`: the manifest enables it, and a release hold turns it off here.
 * - `off`: the manifest does not enable it.
 */
export const featureStateSchema = z.enum(['on', 'held', 'off']);
export type FeatureState = z.infer<typeof featureStateSchema>;

export const featureRowSchema = z.object({
    id: z.string(),
    title: z.string(),
    kind: z.enum(['module', 'feature', 'surface']),
    /** The number of dots in the id: 0 for a layer root. */
    depth: z.number().int(),
    state: featureStateSchema,
    /** The AI seat classes that cover the feature. Empty when no seat gates it. */
    seats: z.array(z.string()),
});
export type FeatureRow = z.infer<typeof featureRowSchema>;

export const featuresResponseSchema = z.object({
    client: z.string(),
    name: z.string(),
    /** The hash of the manifest with no hold, as the server logs it at boot. */
    manifestHash: z.string(),
    packages: z.array(
        z.object({ id: z.string(), title: z.string(), includedIn: z.string().nullable() }),
    ),
    addons: z.array(z.object({ id: z.string(), title: z.string() })),
    grantsAllSeats: z.boolean(),
    aiSeats: z.array(
        z.object({
            seat: z.string(),
            label: z.string(),
            bought: z.boolean(),
            /** The most users that can hold the class, or null for no limit. */
            cap: z.number().int().nullable(),
        }),
    ),
    counts: z.object({
        on: z.number().int(),
        held: z.number().int(),
        total: z.number().int(),
    }),
    layers: z.array(
        z.object({
            kind: z.string(),
            name: z.string(),
            features: z.array(featureRowSchema),
        }),
    ),
});
export type FeaturesResponse = z.infer<typeof featuresResponseSchema>;

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const adminReferenceContract = c.router(
    {
        getAccess: {
            method: 'GET',
            path: '/admin/access',
            responses: { 200: accessGridResponseSchema },
            summary:
                'Admin: who can read and change which data, from the registry of the running server.',
        },
        getFeatures: {
            method: 'GET',
            path: '/admin/features',
            responses: { 200: featuresResponseSchema },
            summary:
                'Admin: the packages, the AI seats and the state of each feature in this deployment.',
        },
    },
    {
        pathPrefix: '/api',
        strictStatusCodes: false,
    },
);
